import { useCallback, useEffect, useRef, useState } from "react";
import { authedFetch } from "../api-client";
import type { SchrijvenTaskType } from "@a2/core";

/**
 * Debounced hook that polls the /schrijven/bullet-coverage endpoint
 * as the user types. Returns an array of booleans, one per bullet,
 * indicating whether the student's text covers each bullet point.
 */
export function useBulletCoverage(
  taskType: SchrijvenTaskType,
  userText: string,
  bullets: string[],
  debounceMs = 1500,
): boolean[] {
  const [covered, setCovered] = useState<boolean[]>(() =>
    bullets.map(() => false),
  );
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const fetchCoverage = useCallback(
    async (text: string) => {
      if (!text.trim() || bullets.length === 0) {
        setCovered(bullets.map(() => false));
        return;
      }
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      try {
        const res = await authedFetch("/schrijven/bullet-coverage", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ taskType, userText: text, bullets }),
          signal: ctrl.signal,
        });
        if (!res.ok) return;
        const body = (await res.json()) as { ok: boolean; data: { covered: boolean[] } };
        if (body.ok) setCovered(body.data.covered);
      } catch {
        // Aborted or network error — silently ignore.
      }
    },
    [taskType, bullets],
  );

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      fetchCoverage(userText).catch(() => undefined);
    }, debounceMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [userText, fetchCoverage, debounceMs]);

  // Reset when bullets change.
  useEffect(() => {
    setCovered(bullets.map(() => false));
  }, [bullets]);

  return covered;
}
